import React, { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/basic/Button/Button';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import FileInput from 'components/basic/inputs/FileInput';
import TextInput from 'components/basic/inputs/TextInput';
import ErrorPanel from 'components/basic/panels/ErrorPanel';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';
import {
  UNLOCK_WALLET_SECRET,
  unlockByKeystoreFile,
} from 'lib/ethereum/wallets/privateKeyWallet';
import { readFileContent } from 'lib/file';

import UnlockSuccess from './shared/UnlockSuccess';
import UnlockLayout from './UnlockLayout';

const SelectedWrap = styled.div`
  background: var(--mainDark);
  border-radius: 5px;
  margin-top: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  .isMobileStyle & {
    padding: 20px;
  }
`;

const UnlockWrap = styled.div`
  padding: 40px 0 10px;
  text-align: center;
`;

type Props = {
  layout: 'page' | 'modal';
  onBack: () => void;
  onUnlock: (
    publicKey: string,
    privateKeyEncrypted: string,
    personalSecret: string
  ) => Promise<void>;
};

function UnlockKeystore({ onUnlock, onBack, layout }: Props) {
  const [errorMessage, setErrorMessage] = useState('');

  const [processing, setProcessing] = useState(false);
  const [unlockedOk, setUnlockedOk] = useState(false);

  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [password, setPassword] = useState('');

  const handleFileChange = (file: File) => {
    if (file.size > 5000) {
      // Keystore has generally 500 bytes so 5k should be safe in any case
      // If we let user use large file, browser might be unresponsize...
      setErrorMessage(
        'File exceeds maximum allowed size. Did you upload the correct file?'
      );
      setFileContent('');
      setFileName('');
      return;
    }
    setFileName(file.name);
    readFileContent(file).then(setFileContent);
  };

  const handleUnlock = () => {
    setProcessing(true);
    if (errorMessage) {
      setErrorMessage('');
    }
    unlockByKeystoreFile(
      { encryptedJson: fileContent, walletPassword: password },
      UNLOCK_WALLET_SECRET
    )
      .then(wallet =>
        onUnlock(wallet.publicKey, wallet.encryptedKey, wallet.personalSecret)
      )
      .then(() => {
        setUnlockedOk(true);
      })
      .catch(error => {
        console.warn(error);
        setPassword('');
        setProcessing(false);
        setErrorMessage(makeEthersErrorHumanRedable(error));
      });
  };

  const unlockDisabled = !(fileContent && password) || processing;

  return (
    <UnlockLayout
      selectedDetail="keystore"
      isQrScren={false}
      onClose={onBack}
      closeDisabled={processing}
      layout={layout}
      isDetail
    >
      {unlockedOk ? (
        <UnlockSuccess />
      ) : (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (!unlockDisabled) {
              handleUnlock();
            }
          }}
        >
          {errorMessage && <ErrorPanel message={errorMessage} marginBottom />}

          <Content>
            <FileInput
              title={
                <>
                  <MaterialSymbol icon="publish" fontSize="default" /> Upload
                  Keystore Wallet File
                </>
              }
              disabled={processing}
              onChange={handleFileChange}
            />

            {fileContent && (
              <SelectedWrap>
                <MaterialSymbol icon="attach_file" />
                <b>{fileName}</b>
              </SelectedWrap>
            )}

            <p style={{ paddingTop: 10, opacity: fileContent ? 1 : 0.5 }}>
              Enter Wallet Password:
            </p>
            <TextInput
              disabled={processing || !fileContent}
              type="password"
              onChange={value => setPassword(value)}
              value={password}
            />

            <UnlockWrap>
              <Button
                inline
                widePadding
                disabled={unlockDisabled}
                onClick={handleUnlock}
              >
                Connect Wallet
              </Button>
            </UnlockWrap>
          </Content>
        </form>
      )}
    </UnlockLayout>
  );
}

export default UnlockKeystore;
